/**
 * Card-face URL mapping for the OpenMoji codepoint set + per-tenant custom
 * cards.
 *
 * Backend transmits each tile's symbol as one of:
 *   - an OpenMoji codepoint string (`1F3AE`, `2B50-FE0F`, etc.). The browser
 *     resolves these to `/cards/openmoji/<CODEPOINT>.svg`, served by nginx
 *     out of `/var/www/cardmatch/cards/openmoji/`.
 *   - a per-tenant custom-card asset URL starting with `/` (specifically
 *     `/tenant-assets/card-match/<tenantId>/<cardId>`). The browser uses
 *     the URL as-is; the backend serves it via the authenticated/proxied
 *     `/tenant-assets/*` route.
 *
 * `iconForSymbol` returns the URL unconditionally — the manifest of valid
 * codepoints is the seeded-shuffle source on the backend, so anything the
 * browser receives via WS is guaranteed to resolve. If a non-codepoint /
 * non-asset string somehow lands (e.g. legacy in-flight game restarting on
 * the new backend), the resulting 404 just shows a blank face — acceptable
 * as a brief one-frame regression rather than a JS crash.
 */

const ICON_BASE = '/cards';
const OPENMOJI_BASE = `${ICON_BASE}/openmoji`;

/**
 * Strict codepoint shape: 1+ hex group(s) joined by `-`. Used to reject any
 * garbage strings (empty, whitespace, embedded slashes) so we never try to
 * fetch `/cards/openmoji/.svg` etc.
 */
const CODEPOINT_RE = /^[0-9A-Fa-f]+(-[0-9A-Fa-f]+)*$/;

/**
 * Custom-card asset path shape: must start with `/tenant-assets/card-match/`
 * + 2 uuid segments. Anything else with a leading slash falls through to the
 * codepoint branch (which will reject + return null).
 */
const CUSTOM_ASSET_RE =
  /^\/tenant-assets\/card-match\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function iconForSymbol(symbol: string): string | null {
  if (!symbol) return null;
  // Custom-card path: rendered verbatim as `<img src=...>` by the browser.
  // The leading `/` and strict UUID format guarantee we don't accidentally
  // accept some other server's URL (e.g. an http:// from a future protocol
  // change).
  if (symbol.startsWith('/')) {
    return CUSTOM_ASSET_RE.test(symbol) ? symbol : null;
  }
  // Otherwise treat as an OpenMoji codepoint -> /cards/openmoji/<cp>.svg.
  if (!CODEPOINT_RE.test(symbol)) return null;
  return `${OPENMOJI_BASE}/${symbol}.svg`;
}

export function cardBackUrl(): string {
  return `${ICON_BASE}/back.svg`;
}
